import React, { useEffect, useState } from 'react';
import BarChart from '../components/BarChart';
import PieChart from '../components/PieChart';
import { declarationData } from '../api/dashboard';
import Loader from '../components/Loader';
import useUserStore from '../store/useUserStore';

const MONTHS_FULL = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const scopeMapping = {
    "CD": "CDS",
    "GVM": "GVMS",
    "FSD": "FSD",
    "RENS": "ENS"
};

export default function Dashboard() {

    const { user } = useUserStore();
    const [apiData, setApiData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const YEARS = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - i);
    const [selectedMonth, setSelectedMonth] = useState(MONTHS_FULL[new Date().getMonth()]);
    const [selectedClientMonth, setSelectedClientMonth] = useState(MONTHS_FULL[new Date().getMonth()]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedStatusUser, setSelectedStatusUser] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [selectedStatusMonth, setSelectedStatusMonth] = useState(MONTHS_FULL[new Date().getMonth()]);


    const [activeUserChart, setActiveUserChart] = useState(null);
    const [userDeclarationsChart, setUserDeclarationsChart] = useState(null);
    const [statusUserDeclarationsChart, setStatusUserDeclarationsChart] = useState(null);

    const [clientDeclarationsChart, setClientDeclarationsChart] = useState(null);

    useEffect(() => {
        let mounted = true;
        async function fetchData() {
            try {
                setLoading(true);
                const res = await declarationData(selectedYear);
                setLoading(false);
                const items = res?.data?.data ?? [];
                if (!mounted) return;
                setApiData(items);

                const monthCounts = Array(12).fill(0);
                const companyNames = [];

                const validStatuses = ["HMRCCleared-AfterCorrections/PhyInsp", "HMRCCleared", "HMRCCleared-DebtCalPending", "HMRCExportExit-Confirmed", "COMPLETED", "ENS Accepted"];
                items.forEach(item => {
                    const status = item.Status_key || item.GVMSStatus_key || "";
                    if (validStatuses.includes(status)) {
                        const idx = new Date(item.createdAt).getMonth();
                        monthCounts[idx]++;
                        if (item.companyName) companyNames.push(item.companyName);
                    }
                });

                setUsers(
                    [...new Set(companyNames.filter(Boolean))]
                        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                );

                setActiveUserChart({
                    series: [{ name: 'Declaration', data: monthCounts }],
                    options: {
                        chart: { type: 'bar' },
                        title: { text: 'Declaration Report', align: 'center' },
                        xaxis: { categories: MONTHS_SHORT },
                        dataLabels: { enabled: false },
                    },
                });

                // default selected user if none
                setSelectedUser(prev => prev || companyNames[0] || '');
                setSelectedStatusUser(prev => prev || companyNames[0] || '');
            } catch (err) {
                // keep quiet in UI; log for debugging
                // eslint-disable-next-line no-console
                console.error('Failed to load declaration data', err);
            }
        }

        fetchData();
        return () => { mounted = false; };
    }, [selectedYear, user.db]);

    useEffect(() => {
        if (!apiData.length || !selectedUser) {
            setUserDeclarationsChart(null);
            return;
        }

        const scopedBaseStatusFilter = {
            "CD": {
                "condition": "includes",
                "field": "Status_key",
                "value": ["HMRCCleared-AfterCorrections/PhyInsp", "HMRCCleared", "HMRCCleared-DebtCalPending", "HMRCExportExit-Confirmed"]
            },
            "GVM": {
                "condition": "includes",
                "field": "GVMSStatus_key",
                "value": ["COMPLETED"]
            },
            "FSD": {
                "condition": "includes",
                "field": "Status_key",
                "value": ["HMRCCleared-AfterCorrections/PhyInsp", "HMRCCleared", "HMRCCleared-DebtCalPending", "HMRCExportExit-Confirmed"]
            },
            "RENS": {
                "condition": "includes",
                "field": "Status_key",
                "value": ["ENS Accepted"]
            }
        }

        const scoped = apiData.reduce((acc, item) => {
            const itemMonth = new Date(item.createdAt).toLocaleString('default', { month: 'long' });
            if (item.companyName === selectedUser && itemMonth === selectedMonth) {
                let includeItem = true;

                // Apply filter only if config exists for this scope
                const filterConfig = scopedBaseStatusFilter[item.declarationScope_key];

                if (filterConfig) {
                    const fieldValue = item[filterConfig.field];

                    if (filterConfig.condition === "includes") {
                        includeItem = filterConfig.value.includes(fieldValue);
                    }

                    if (filterConfig.condition === "excludes") {
                        includeItem = !filterConfig.value.includes(fieldValue);
                    }
                }

                // Only count if passes filter
                if (includeItem) {
                    acc[scopeMapping[item.declarationScope_key] || item.declarationScope_key] = (acc[scopeMapping[item.declarationScope_key] || item.declarationScope_key] || 0) + 1;
                }
            }
            return acc;
        }, {});

        const declarationTypes = Object.keys(scoped);
        const declarationCounts = Object.values(scoped);

        const total = declarationCounts.reduce((sum, val) => sum + val, 0);
        setUserDeclarationsChart({
            series: [{ name: 'Declarations', data: declarationCounts }],
            options: {
                chart: { type: 'bar' },
                title: { text: `${total} Declarations`, align: 'center' },
                xaxis: { categories: declarationTypes },
                dataLabels: { enabled: false },
            },
        });
    }, [apiData, selectedMonth, selectedUser]);

    useEffect(() => {
        if (!apiData.length || !selectedStatusUser) {
            setStatusUserDeclarationsChart(null);
            return;
        }

        const scoped = apiData.reduce((acc, item) => {
            const itemMonth = new Date(item.createdAt).toLocaleString('default', { month: 'long' });
            if (item.companyName === selectedStatusUser && itemMonth === selectedStatusMonth) {
                acc[item.Status_key] = (acc[item.Status_key] || 0) + 1;
            }
            return acc;
        }, {});

        const declarationTypes = Object.keys(scoped);
        const declarationCounts = Object.values(scoped);

        const total = declarationCounts.reduce((sum, val) => sum + val, 0);
        setStatusUserDeclarationsChart({
            series: [{ name: 'Declarations', data: declarationCounts }],
            options: {
                chart: { type: 'bar' },
                title: { text: `${total} Declarations`, align: 'center' },
                xaxis: { categories: declarationTypes },
                dataLabels: { enabled: false },
            },
        });
    }, [apiData, selectedStatusMonth, selectedStatusUser]);

    useEffect(() => {
        if (!apiData.length || !selectedUser) {
            setClientDeclarationsChart(null);
            return;
        }

        const scoped = apiData.reduce((acc, item) => {
            const itemMonth = new Date(item.createdAt).toLocaleString('default', { month: 'long' });
            if (itemMonth === selectedClientMonth) {
                acc[item.companyName] = (acc[item.companyName] || 0) + 1;
            }
            return acc;
        }, {});

        const declarationTypes = Object.keys(scoped);
        const declarationCounts = Object.values(scoped);

        const total = declarationCounts.reduce((sum, val) => sum + val, 0);
        setClientDeclarationsChart({
            series: [{ name: 'Declarations', data: declarationCounts }],
            options: {
                chart: { type: 'bar' },
                title: { text: `${total} Declarations`, align: 'center' },
                xaxis: { categories: declarationTypes },
                dataLabels: { enabled: false },
            },
        });
    }, [apiData, selectedClientMonth]);
    return (

        <div className="content-wrapper">
            {loading && <Loader />}
            <div className="page-header">
                <div className="form-group col-lg-4">
                    <label >Select Year</label>
                    <select
                        className="form-select"
                        id="selectedYear"
                        name="selectedYear"
                        value={selectedYear}
                        onChange={e => setSelectedYear(e.target.value)}
                    >
                        {YEARS.map((name) => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="row">

                <div className="col-md-6 grid-margin stretch-card">
                    <div className="card">
                        <div className="card-body">
                            <BarChart data={activeUserChart} />
                        </div>
                    </div>
                </div>
                <div className="col-md-6 grid-margin stretch-card">
                    <div className="card">
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label htmlFor="month">Select Month</label>
                                    <select
                                        className="form-select"
                                        id="month"
                                        name="month"
                                        value={selectedMonth}
                                        onChange={e => setSelectedMonth(e.target.value)}
                                    >
                                        {MONTHS_FULL.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-8">
                                    <label>Select User</label>
                                    <select
                                        className="form-select"
                                        id="selectedUser"
                                        name="selectedUser"
                                        value={selectedUser}
                                        onChange={e => setSelectedUser(e.target.value)}
                                    >
                                        {users.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <BarChart data={userDeclarationsChart} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">


                <div className="col-md-6 grid-margin stretch-card">
                    <div className="card">
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label htmlFor="month">Select Month</label>
                                    <select
                                        className="form-select"
                                        id="month"
                                        name="month"
                                        value={selectedClientMonth}
                                        onChange={e => setSelectedClientMonth(e.target.value)}
                                    >
                                        {MONTHS_FULL.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>

                            </div>
                            {/* <BarChart data={clientDeclarationsChart} /> */}
                            <PieChart data={clientDeclarationsChart} />
                        </div>
                    </div>
                </div>
                <div className="col-md-6 grid-margin stretch-card">
                    <div className="card">
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label htmlFor="month">Select Month</label>
                                    <select
                                        className="form-select"
                                        id="month"
                                        name="month"
                                        value={selectedStatusMonth}
                                        onChange={e => setSelectedStatusMonth(e.target.value)}
                                    >
                                        {MONTHS_FULL.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-8">
                                    <label>Select User</label>
                                    <select
                                        className="form-select"
                                        id="selectedUser"
                                        name="selectedUser"
                                        value={selectedStatusUser}
                                        onChange={e => setSelectedStatusUser(e.target.value)}
                                    >
                                        {users.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <PieChart data={statusUserDeclarationsChart} />
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}